import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { studyProcess, getStudyStatus, StudyAssetStatus } from "@/api/studio/uploads/api";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";
import { StudiesLayout } from "@/components/layouts";
import { toast } from "@/components/ui/toast";
import { VideoReportsSkeleton } from "@/features/reports/components/video-reports-skeleton";
import { AnalysingVideosModal } from "@/features/studies/modals/analysing-videos-modal";

/* Prop Types */
interface StudyVideoProcessRouteProps {
  projectId: string;
  studyId: string;
}

const POLL_INTERVAL = 3000;

/**
 * StudyVideoProcessRoute component
 *
 * Triggers video study processing and polls status until complete, then navigates to reports.
 */
export const StudyVideoProcessRoute = ({ projectId, studyId }: StudyVideoProcessRouteProps) => {
  const hasStarted = useRef(false);

  /* State */
  const [assetStatuses, setAssetStatuses] = useState<StudyAssetStatus[]>([]);
  const [isActive, setIsActive] = useState(false);

  /* Hooks */
  const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-process");
  const [, navigate] = useLocation();

  /* Derived state */
  const succeededCount = assetStatuses.filter((a) => a.status === "SUCCEEDED").length;
  const totalCount = assetStatuses.length;

  /* Handlers */
  const handleClose = () => navigate(`/projects/${projectId}`);

  /* Side Effects */
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const startProcessing = async () => {
      try {
        setIsActive(true);
        await studyProcess(projectId, studyId);

        // Poll until all assets reach a terminal state (SUCCEEDED or FAILED)
        while (true) {
          const statuses = await getStudyStatus(projectId, studyId, "process");
          setAssetStatuses(statuses);

          const isFinished = (status: string) => status === "SUCCEEDED" || status === "FAILED";
          if (statuses.length > 0 && statuses.every((a) => isFinished(a.status))) {
            break;
          }

          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
        }

        navigateToNext();
      } catch {
        toast({
          title: "We couldn't process your video, please try again.",
          type: "error",
        });
      }
    };

    void startProcessing();
  }, []);

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="reports">
      <VideoReportsSkeleton />
      {isActive && <AnalysingVideosModal succeededCount={succeededCount} totalCount={totalCount} onClose={handleClose} />}
    </StudiesLayout>
  );
};
